<script setup lang="ts">
import { computed, ref, watch, onUnmounted, onMounted, onBeforeMount, nextTick } from 'vue'
import { storeToRefs } from 'pinia'
import {
  // useThemeStore,
  useValueDataStore
} from '@/stores'
import {
  EntityType,
  // type Group,
  // type Store,
  type StoreSummary
} from '@/data'

import ContentView from './ContentView.vue'
import SpinnerOverlay from '@/components/common/SpinnerOverlay.vue'
import SalesRepContactButton from '@/components/common/SalesRepContactButton.vue'
import ExportButton from '@/components/common/ExportButton.vue'
import ExternalLinks from '@/components/common/ExternalLinks.vue'

import ErrorView from './ErrorView.vue'
// import DataRefreshDatesButton from '@/components/common/DataRefreshDatesButton.vue'
import { formatCurrency } from '@/components/common/common'
import HeaderItem from '@/components/widgets/stores-list/HeaderItem.vue'
import SearchBar from '@/components/widgets/stores-list/SearchBar.vue'

export type SortField =
  | 'storeName'
  | 'dbgValue'
  | 'arrotexValue'
  | 'vidacorpValue'
  | 'axeValue'
  | null
type SortDirection = 'asc' | 'desc' | null

// const themeStore = useThemeStore()
// const { themeColors } = themeStore
const { valueData, isValueDataStoreLoading, isError, filteredGroupId, searchValue } =
  storeToRefs(useValueDataStore())
const { loadValueData, getStoresSummaryByGroupId } = useValueDataStore()
const storeSummaryList = ref<StoreSummary[]>([])
// const searchValue = ref<string>('')

const selectedStoreName = ref<string | null>(valueData.value?.customerName || null)

// Reference to the table element
const tableElement = ref<HTMLElement | null>(null)
const currentPage = ref(1)
const rowsPerPage = ref(8)

const sortField = ref<SortField>(null)
const sortDirection = ref<SortDirection>(null)

const setSort = (field: string) => {
  const typedField = field as SortField
  if (sortField.value !== typedField) {
    sortField.value = typedField
    sortDirection.value = 'asc'
  } else if (sortDirection.value === 'asc') {
    sortDirection.value = 'desc'
  } else if (sortDirection.value === 'desc') {
    sortField.value = null
    sortDirection.value = null
  } else {
    sortDirection.value = 'asc'
  }
}

const updateRowsPerPage = () => {
  if (!tableElement.value) return

  const tableHeight = tableElement.value.clientHeight

  const remHeight = 3.75 // 60px at 16px base
  const base = parseFloat(getComputedStyle(document.documentElement).fontSize || '16')
  const minRowHeight = remHeight * base
  const newRowsPerPage = Math.floor(tableHeight / minRowHeight)

  rowsPerPage.value = Math.max(newRowsPerPage, 1)
}

// Handle window resize (Zoom In/Out)
const handleResize = () => {
  updateRowsPerPage()
}

onBeforeMount(() => {
  window.addEventListener('resize', handleResize)
})

onMounted(async () => {
  await nextTick() // Ensures Vue has fully rendered the DOM before calculations
  updateRowsPerPage()
})

onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})

watch(
  isValueDataStoreLoading,
  async (loading) => {
    if (!loading) {
      await nextTick()
      if (paginatedData.value.length > 0 && !selectedStoreName.value) {
        selectedStoreName.value = paginatedData.value[0]!.storeName
      }
      storeSummaryList.value = getStoresSummaryByGroupId()
      requestAnimationFrame(() => updateRowsPerPage())
    }
  },
  { immediate: true }
)

watch(
  () => filteredGroupId.value,
  () => {
    selectedStoreName.value = filteredGroupId.value
      ? null
      : valueData.value?.customerName || null // Reset selected store name if group filter changes
    storeSummaryList.value = getStoresSummaryByGroupId()
  }
)

watch(
  () => searchValue.value,
  () => {
    storeSummaryList.value = getStoresSummaryByGroupId()
  }
)

watch(
  () => storeSummaryList.value,
  () => {
    currentPage.value = 1 // Reset pagination on data change
    if (currentPage.value > totalPages.value) {
      currentPage.value = totalPages.value || 1
    }

    updateRowsPerPage() // Update table row calculations
  }
)

watch(
  () => filteredGroupId.value,
  async () => {
    selectedStoreName.value = null
    currentPage.value = 1
    await nextTick()
    updateRowsPerPage()
  }
)

const totalPages = computed(() => {
  if (!storeSummaryList.value) return 0
  return Math.ceil(storeSummaryList.value.length / rowsPerPage.value)
})

const paginatedData = computed(() => {
  if (!storeSummaryList.value) return []
  if (storeSummaryList.value.length === 0) return []

  let sortedData = [...storeSummaryList.value]

  if (sortField.value && sortDirection.value) {
    sortedData.sort((a, b) => {
      let valueA = a[sortField.value!] ?? 0
      let valueB = b[sortField.value!] ?? 0

      // For storeName, do string comparison
      if (sortField.value === 'storeName') {
        valueA = String(valueA).toLowerCase()
        valueB = String(valueB).toLowerCase()
        if (valueA < valueB) return sortDirection.value === 'asc' ? -1 : 1
        if (valueA > valueB) return sortDirection.value === 'asc' ? 1 : -1
        return 0
      }

      // For numbers
      const numA = Number(valueA) || 0
      const numB = Number(valueB) || 0
      return sortDirection.value === 'asc' ? numB - numA : numA - numB
    })
  } else {
    // Default sort by dbgValue descending
    sortedData.sort((a, b) => (b.dbgValue || 0) - (a.dbgValue || 0))
  }

  // Pagination logic
  const start = (currentPage.value - 1) * rowsPerPage.value
  return sortedData.slice(start, start + rowsPerPage.value)
})

const aggregateRow = computed(() => {
  if (!filteredGroupId.value) return null
  const list = getStoresSummaryByGroupId()
  if (!list.length) return null

  return list.reduce(
    (acc, item) => ({
      storeName: acc.storeName,
      dbgValue: acc.dbgValue + (item.dbgValue || 0),
      dbgOpportunity: acc.dbgOpportunity + (item.dbgOpportunity || 0),
      arrotexValue: acc.arrotexValue + (item.arrotexValue || 0),
      arrotexOpportunity: acc.arrotexOpportunity + (item.arrotexOpportunity || 0),
      vidacorpValue: acc.vidacorpValue + (item.vidacorpValue || 0),
      vidacorpOpportunity: acc.vidacorpOpportunity + (item.vidacorpOpportunity || 0),
      axeValue: acc.axeValue + (item.axeValue || 0),
      axeOpportunity: acc.axeOpportunity + (item.axeOpportunity || 0)
    }),
    {
      storeName: 'Group Total',
      dbgValue: 0,
      dbgOpportunity: 0,
      arrotexValue: 0,
      arrotexOpportunity: 0,
      vidacorpValue: 0,
      vidacorpOpportunity: 0,
      axeValue: 0,
      axeOpportunity: 0
    }
  )
})

// Pagination buttons
const displayedPages = computed(() => {
  if (totalPages.value) {
    const pages: (number | string)[] = []
    const lastPage = totalPages.value

    if (lastPage <= 4) {
      for (let i = 1; i <= lastPage; i++) {
        pages.push(i)
      }
    } else {
      if (currentPage.value < 3) {
        pages.push(1, 2, 3, '...', lastPage)
      } else if (currentPage.value > lastPage - 2) {
        pages.push(1, '...', lastPage - 2, lastPage - 1, lastPage)
      } else {
        pages.push(
          1,
          '...',
          currentPage.value - 1,
          currentPage.value,
          currentPage.value + 1,
          '...',
          lastPage
        )
      }
    }

    return pages
  }
  return []
})

const goToPage = (page: number | string) => {
  if (typeof page === 'number') {
    currentPage.value = page
  }
}

const handleLoadData = (type: 'group' | 'store', id: string, name: string) => {
  if (type === 'group') {
    loadValueData(EntityType.GROUP, id)
  } else if (type === 'store') {
    loadValueData(EntityType.STORE, id)
    selectedStoreName.value = name
  }
}

</script>

<template>
  <SpinnerOverlay v-if="isValueDataStoreLoading" />
  <ErrorView v-else-if="isError" />
  <ContentView v-else>
    <template #top>
      <!-- <DataRefreshDatesButton /> -->
      <ExternalLinks />
      <SalesRepContactButton label="Contact your Arrotex Rep" type="arrotex" />
      <SalesRepContactButton label="Contact your VidaCorp Rep" type="vidacorp" />
      <ExportButton />
    </template>
    <template #middle>
      <div class="stores-list-container">
        <div class="search-bar-container">
          <SearchBar v-model:search="searchValue" />
        </div>

        <div
          v-if="aggregateRow"
          class="store-list-item aggregate"
          :class="{ selected: false }"
        >
          <div>
            <div class="store-name-cell no-tooltip">
              <span class="store-name">{{ aggregateRow.storeName }}</span>
            </div>
          </div>
          <div class="value-item-cell">
            <span class="value">{{ formatCurrency(aggregateRow.dbgValue) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(aggregateRow.dbgOpportunity) }}</span>
          </div>
          <div class="value-item-cell">
            <span class="value">{{ formatCurrency(aggregateRow.arrotexValue) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(aggregateRow.arrotexOpportunity) }}</span>
          </div>
          <div class="value-item-cell">
            <span class="value">{{ formatCurrency(aggregateRow.vidacorpValue) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(aggregateRow.vidacorpOpportunity) }}</span>
          </div>
          <div class="value-item-cell">
            <span class="value">{{ formatCurrency(aggregateRow.axeValue) }}</span>
            <span class="value">/</span>
            <span class="opportunity">{{ formatCurrency(aggregateRow.axeOpportunity) }}</span>
          </div>
        </div>

        <div class="store-list-item header" :class="{margin: !aggregateRow}">
          <HeaderItem
            fieldName="Store Name"
            fieldKey="storeName"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            fieldName="DBG"
            fieldKey="dbgValue"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            fieldName="Arrotex"
            fieldKey="arrotexValue"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            fieldName="VidaCorp"
            fieldKey="vidacorpValue"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
          <HeaderItem
            fieldName="AXE"
            fieldKey="axeValue"
            :sortField="sortField"
            :sortDirection="sortDirection"
            :clickAction="setSort"
          />
        </div>
        <div class="inner-container" ref="tableElement">
          <div
            class="store-list-item"
            v-for="store in paginatedData"
            :key="store.storeId"
            @click="handleLoadData('store', store.storeId, store.storeName)"
            :class="{ selected: store.storeName === selectedStoreName }"
          >
            <div class="store-name-cell" :data-full-name="store.storeName">
              <span class="store-name">{{ store.storeName }}</span>
            </div>
            <div class="value-item-cell">
              <span class="value">{{ formatCurrency(store.dbgValue) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(store.dbgOpportunity) }}</span>
            </div>
            <div class="value-item-cell">
              <span class="value">{{ formatCurrency(store.arrotexValue) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(store.arrotexOpportunity) }}</span>
            </div>
            <div class="value-item-cell">
              <span class="value">{{ formatCurrency(store.vidacorpValue) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(store.vidacorpOpportunity) }}</span>
            </div>
            <div class="value-item-cell">
              <span class="value">{{ formatCurrency(store.axeValue) }}</span>
              <span class="value">/</span>
              <span class="opportunity">{{ formatCurrency(store.axeOpportunity) }}</span>
            </div>
          </div>
        </div>
        <!-- Pagination Controls -->
        <div class="pagination">
          <button
            v-for="page in displayedPages"
            :key="page"
            :disabled="page === '...'"
            :class="{ active: page === currentPage }"
            @click="goToPage(page)"
          >
            {{ page === '...' ? '...' : `Page ${page}` }}
          </button>
        </div>
      </div>
    </template>
  </ContentView>
</template>

<style scoped>
.stores-list-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  width: 100%;
  height: 85vh;
}

.search-bar-container {
  align-self: flex-start;
  width: 33.33%;
}

.inner-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  flex-direction: column;
  align-items: center;
  justify-content: start;
  display: flex;
  overflow-y: auto;
}

.store-list-item {
  text-align: center;
  display: grid;
  grid-template-columns: 30% repeat(4, 17.5%);
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 3.125rem;
  padding: 0.75rem;
  border-radius: 25rem;
  transition: background-color 0.5s ease;
  background-color: var(--color-background-bright);
  cursor: pointer;
  margin-bottom: 0.75rem;
}

.store-list-item.aggregate {
  background-color: var(--color-background-bright);
  color: var(--color-text);
  cursor: default;
  margin-top: 2rem;
  border-radius: 5rem;
}

.store-list-item.aggregate  .store-name-cell{
  font-weight: 900;
}

.store-list-item > div:first-child {
  text-align: start;
  display: flex;
  align-items: center;
  width: 100%;
}

.store-list-item.aggregate .value {
  color: var(--color-text);
  font-size: var(--font-size-body);
}

.store-name-cell {
  width: 100%;
  position: relative;
}

.value-item-cell{ 
  display: flex;
  align-items: center;
  justify-content: center;
}

.store-name-cell.no-tooltip::after {
  display: none;
}

.store-list-item.header {
  font-size: var(--font-size-small);
  padding: 0;
  padding-inline: 0.75rem;
  background-color: unset;
  height: 5%;
  cursor: default;
}

.store-list-item.header .store-name {
  font-size: var(--font-size-small);
}

.store-list-item.header.margin {
  margin-top: 2rem;
}

.store-list-item.selected {
  background-color: var(--color-text);
  color: var(--color-background-bright);
}

.store-list-item.selected .value {
  color: var(--color-background-bright);
}

.store-list-item .store-name {
  display: block;
  width: 100%;
  padding-left: 0.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: var(--font-size-body);
}

.store-name-cell::after {
  content: attr(data-full-name);
  font-size: var(--font-size-tooltip);
  line-height: 1.4;
  position: absolute;
  top: calc(100%);
  left: 0;
  background: var(--color-background-bright, #ffffff);
  color: var(--text-color, #414141);
  border: 0.0625rem solid var(--color-background, #e0e0e0);
  padding: 0.5rem 0.625rem;
  border-radius: 0.625rem;
  min-width: 12.5rem;
  max-width: 20rem;
  white-space: normal;
  word-break: normal;
  box-shadow: 0 0.125rem 0.5rem rgba(0, 0, 0, 0.08);
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.12s ease, visibility 0.12s ease;
  z-index: 1000;
  text-align: left;
}

.store-name-cell:hover::after {
  opacity: 1;
  visibility: visible;
}

.store-list-item .header-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.store-list-item .value {
  font-weight: 700;
  font-size: var(--font-size-body);
  color: var(--color-text);
}

.store-list-item .opportunity {
  font-weight: 700;
  font-size: var(--font-size-tooltip);
  color: var(--color-vidacorp-3);
}
.pagination {
  margin-top: 0.625rem;
  display: flex;
  justify-content: center;
  gap: 0.625rem;
  height: 5%;
}
.pagination button {
  padding: 0.3125rem 0.625rem;
  cursor: pointer;
  font-size: var(--font-size-caption);
  color: var(--color-text);
  opacity: 0.35;
}
.pagination button.active {
  color: var(--color-text);
  opacity: 1;
}
.pagination button:hover {
  color: var(--color-text);
  opacity: 1;
}
</style>
